import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Radio, GitBranch, Users, Cpu, LogOut, Sparkles, Bookmark, Mail, PenSquare } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { Mascot } from "@/components/Mascot";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/dashboard", label: "Feed", icon: Radio, testid: "nav-feed", color: "#FF8C42" },
  { to: "/dashboard/trend", label: "Tendance du jour", icon: Sparkles, testid: "nav-trend", color: "#FFD600" },
  { to: "/dashboard/core-updates", label: "Core Updates", icon: GitBranch, testid: "nav-core-updates", color: "#4EA8FF" },
  { to: "/dashboard/ai-watch", label: "Veille IA", icon: Cpu, testid: "nav-ai-watch", color: "#FFA85C" },
  { to: "/dashboard/experts", label: "Experts", icon: Users, testid: "nav-experts", color: "#E56E1F" },
  { to: "/dashboard/bookmarks", label: "Favoris", icon: Bookmark, testid: "nav-bookmarks", color: "#4EA8FF" },
  { to: "/dashboard/newsletter", label: "Récap hebdo", icon: Mail, testid: "nav-newsletter", color: "#FF8C42" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => { await logout(); navigate("/", { replace: true }); };
  const goSubscribe = () => { if (!user?.subscribed) navigate("/#pricing"); };

  return (
    <div className="min-h-screen text-[#FAF0DE] flex relative bg-[#0A1225]">
      {/* Ambient blobs */}
      <div className="blob halo bg-[#FF8C42] w-[500px] h-[500px] -top-40 -left-40 opacity-25" />
      <div className="blob halo bg-[#4EA8FF] w-[400px] h-[400px] top-[60%] right-0 opacity-15" style={{ animationDelay: "2s" }} />

      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col sticky top-0 h-screen z-20 border-r border-[#FAF0DE]/5 bg-[#0A1225]/80 backdrop-blur-xl">
        <div className="px-5 py-5 border-b border-[#FAF0DE]/5">
          <Link to="/" className="flex items-center gap-3" data-testid="sidebar-logo">
            <Mascot pose="waving" className="w-11 h-11" />
            <div>
              <Wordmark className="h-5" text="#FAF0DE" accent="#FF8C42" />
              <div className="text-[10px] text-[#FAF0DE]/40 uppercase tracking-[0.25em] mt-1 font-mono">Live · FR</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid, color }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive ? "text-[#FAF0DE] bg-[#FAF0DE]/[0.06]" : "text-[#FAF0DE]/60 hover:text-[#FAF0DE] hover:bg-[#FAF0DE]/[0.03]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                    style={{
                      background: isActive ? `${color}22` : "rgba(250,240,222,0.03)",
                      boxShadow: isActive ? `0 0 20px -8px ${color}` : "none",
                    }}
                  >
                    <Icon className="w-4 h-4" style={{ color: isActive ? color : "currentColor" }} />
                  </span>
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
          {user?.is_admin && (
            <>
              <div className="px-3 pt-4 pb-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FAF0DE]/30">Admin</div>
              <NavLink
                to="/dashboard/admin/blog"
                data-testid="nav-admin-blog"
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive ? "text-[#FAF0DE] bg-[#FAF0DE]/[0.06]" : "text-[#FAF0DE]/60 hover:text-[#FAF0DE] hover:bg-[#FAF0DE]/[0.03]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: isActive ? "#FF8C4222" : "rgba(250,240,222,0.03)", boxShadow: isActive ? "0 0 20px -8px #FF8C42" : "none" }}>
                      <PenSquare className="w-4 h-4" style={{ color: isActive ? "#FF8C42" : "currentColor" }} />
                    </span>
                    <span>Gérer le blog</span>
                  </>
                )}
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-[#FAF0DE]/5">
          {user?.subscribed ? (
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#FFD600] font-mono flex items-center gap-2" data-testid="sidebar-plan-active">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFD600]" /> Accès bêta gratuit
            </div>
          ) : (
            <button
              onClick={goSubscribe}
              data-testid="sidebar-upgrade-btn"
              className="w-full text-left px-4 py-3 text-xs font-mono uppercase tracking-widest rounded-xl bg-gradient-to-r from-[#FF8C42] to-[#FFD600] text-[#0A1225] hover:opacity-90 transition-opacity"
            >
              Se connecter · gratuit
            </button>
          )}
          {user && (
            <div className="mt-3 flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-[#FAF0DE]/[0.03]">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-2 ring-[#FF8C42]/40" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#FF8C42]/20 flex items-center justify-center text-xs text-[#FF8C42]">
                  {user.name?.[0] || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{user.name}</div>
                <div className="text-[10px] text-[#FAF0DE]/40 truncate">{user.email}</div>
              </div>
              <button onClick={onLogout} data-testid="sidebar-logout-btn" className="text-[#FAF0DE]/40 hover:text-[#FAF0DE]">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#0A1225]/90 backdrop-blur-xl border-b border-[#FAF0DE]/5 h-14 flex items-center px-4">
        <Link to="/" className="flex items-center gap-2">
          <Mascot pose="waving" className="w-8 h-8" />
          <Wordmark className="h-4" text="#FAF0DE" accent="#FF8C42" />
        </Link>
        <div className="ml-auto"><NotificationsBell /></div>
      </div>

      {/* Main */}
      <div className="flex-1 min-w-0 pt-14 lg:pt-0 relative z-10" data-testid="dashboard-main">
        <div className="hidden lg:flex sticky top-0 z-30 h-14 bg-[#0A1225]/70 backdrop-blur-xl border-b border-[#FAF0DE]/5 items-center justify-end px-6 gap-3">
          <NotificationsBell />
        </div>
        <div className="lg:hidden overflow-x-auto whitespace-nowrap px-3 py-2 border-b border-[#FAF0DE]/5 bg-[#0A1225]/95">
          {NAV.map(({ to, label, testid, color }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              data-testid={`m-${testid}`}
              className={({ isActive }) =>
                `inline-block text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full font-mono mr-1 border ${
                  isActive ? "border-[#FAF0DE] text-[#FAF0DE]" : "text-[#FAF0DE]/50 border-[#FAF0DE]/10"
                }`
              }
              style={({ isActive }) => (isActive ? { background: `${color}22`, borderColor: `${color}55`, color } : {})}
            >
              {label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
        <main>{children}</main>
      </div>
    </div>
  );
}
